import React, { useState } from 'react';
import Hero from '../../components/Hero/Hero';
import Pathfinder from '../../components/Pathfinder/Pathfinder';
import Metadata from '../../components/Metadata/Metadata';
import { Box, Typography } from '@mui/material';
import Wallpaper from '../../components/Wallpaper/Wallpaper';
import Section from '../../components/Section/Section';
import Card from '../../components/Card/Card';
import GlobalCallout from '../../components/Global/CalloutDefault';
import Accordion from '../../components/Accordion/Accordion';
import Button from '../../components/Button/Button';
import Image from '../../components/Image/Image';
import ContentSplit from '../../components/ContentSplit/ContentSplit';
import ListItem from '../../components/ListItem/ListItem';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="Careers | General Genomics"
        description="Explore career opportunities at General Genomics and join a team dedicated to advancing bioscience and AI. Learn about our innovative culture, focus on professional growth, and discover current job openings on our LinkedIn job postings."
      />
      <Hero
        helper="About Us"
        headline="Explore career opportunities at General Genomics"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Join Our Innovative Team
        </h2>
        <p className="action-on-view action-fade-in">
          At General Genomics, we offer exciting career opportunities that allow
          you to grow professionally while contributing to the advancement of
          bioscience and AI technologies. Explore how you can make a meaningful
          impact in an innovative, collaborative environment. We’re seeking
          passionate individuals eager to take their career to the next level.
          Ready to start your journey?
        </p>
        <Image
          path="/images/library/office-data-3.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/military-mechanic-man-repair-5.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">
          Explore Our Careers
        </p>
        <h2 className="action-on-view action-fade-in">
          Why Work at General Genomics?
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Innovative Culture:</strong> Be part of a forward-thinking
            team focused on cutting-edge bioscience and AI technologies.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Career Growth:</strong> We prioritize your professional
            development with opportunities to learn and advance your skills.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Collaborative Environment:</strong> Work alongside
            passionate and skilled colleagues who value teamwork and
            collaboration.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Meaningful Impact:</strong> Contribute to projects that
            directly influence the future of healthcare and technology.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">Explore More About Us</h2>
        <p className="action-on-view action-fade-in">
          Discover our mission, methodologies, and how you can contribute to our
          innovative general genomics work. Learn more about our opportunities
          and vision.
        </p>
        <div className="card-group">
          <Card
            icon="business"
            headline="Our Story"
            subhead="Learn about our mission and vision, and how we’re shaping the future of general genomics."
            btnURL="/about-us"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="trending_up"
            headline="Our Approach"
            subhead="Explore the unique methodologies and cutting-edge technologies we use in general genomics."
            btnURL="/our-approach"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="question_answer"
            headline="Contact Us"
            subhead="Have questions about opportunities or the application process? Get in touch!"
            btnURL="/contact-us"
            btnLabel="Get in touch"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../images/library/office-group-presentation.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about career opportunities at General
          Genomics
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Learn about our innovative culture, career benefits, and current job
          openings at General Genomics.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What is the work culture like at General Genomics?"
          >
            At General Genomics, we foster an innovative and collaborative work
            environment that encourages professional growth, creativity, and
            impact in the fields of bioscience and AI.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can I apply for a job at General Genomics?"
          >
            You can explore and apply for current job openings by visiting our{' '}
            <a
              target="_blank"
              href="https://www.linkedin.com/company/generalgenomics/jobs/"
              rel="noreferrer"
            >
              LinkedIn page.
            </a>{' '}
            Simply click on the &quot;View our current openings on{' '}
            <a
              target="_blank"
              href="https://www.linkedin.com/company/generalgenomics/jobs/"
              rel="noreferrer"
            >
              LinkedIn&quot;
            </a>{' '}
            link to access available positions.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can I learn more about General Genomics’ mission and approach?"
          >
            To learn more about our mission and unique methodologies, you can
            visit our <Link to="/our-approach">&quot;Our Approach&quot;</Link>{' '}
            page for more in-depth information.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
